/**
 * Trava de edição da Proposta PME.
 *
 * Assim que a coleta de assinaturas começa, o backend sobe a proposta de
 * "Em digitação" (6) ou "Proposta Aceita" (7) para "Em assinatura" (12) —
 * mesma regra do Capital Global, ver `isCapitalGlobalProposalEditable`. A
 * partir daí nada mais pode ser editado: qualquer alteração de prêmio
 * invalidaria as assinaturas já coletadas.
 *
 * `hasSignaturesStarted` entra no cálculo porque logo após o "Confirmar" o
 * snapshot da proposta em cache ainda pode trazer o status anterior (o refetch
 * dos detalhes é assíncrono). Sem ele a tela ficaria editável por uma janela em
 * que os envelopes já existem.
 *
 * "Proposta Aceita" (7) é EDITÁVEL de propósito: é o status que a aprovação da
 * subscrição grava, devolvendo a proposta ao corretor para ele conferir o
 * Resumo e disparar a assinatura pelo Confirmar. No backend o 7 também é o
 * marcador que impede esse Confirmar de re-rodar a subscrição
 * (`InitiateGroupLifeDocusignSignaturesHandler`).
 */

import {
  isTerminalPortalStatus,
  PORTAL_STATUS_LABELS,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';

/** Copy do PME para o StatusPortal 7 — ver `resolvePmeProposalStatusLabel`. */
export const PME_PROPOSAL_ACCEPTED_LABEL = 'Proposta Aceita';

type PmeProposalLockInput = {
  statusPortal?: string | number | null;
  /** Já existe envelope DocuSign criado para esta proposta. */
  hasSignaturesStarted?: boolean;
};

/** Status da proposta ainda desconhecido (snapshot não carregado). */
function parseStatusPortal(
  statusPortal?: string | number | null
): number | null {
  if (statusPortal == null || statusPortal === '') return null;
  const parsed = Number(statusPortal);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Status em que a proposta PME ainda está nas mãos do corretor — antes da
 * coleta de assinaturas. Editar uma proposta já aceita é permitido de
 * propósito: qualquer alteração de vidas que mexa na tarifa devolve a proposta
 * à subscrição no próximo Confirmar (marcador do `GroupLifeLivesReviewStore`).
 */
const PME_ACTIVE_STATUSES: readonly PortalStatus[] = [
  PortalStatus.ProposalInProgress,
  PortalStatus.ProposalAccepted,
];

/**
 * Proposta PME editável — "Em digitação" ou "Proposta Aceita", e sem assinatura
 * iniciada. Status ausente não libera edição por si: quem decide é o `mode` da
 * rota.
 */
export function isPmeProposalEditable({
  statusPortal,
  hasSignaturesStarted = false,
}: PmeProposalLockInput): boolean {
  if (hasSignaturesStarted) return false;

  const status = parseStatusPortal(statusPortal);
  if (status == null) return true;

  return PME_ACTIVE_STATUSES.includes(status);
}

/**
 * Assinaturas concluídas e a proposta ainda "Em assinatura" (12): a transmissão
 * é automática (o backend transmite assim que fica sabendo das assinaturas, pelo
 * webhook ou pelo sync) e ainda não aconteceu. A tela mantém o "Atualizar todos"
 * — é ele que tenta a transmissão de novo — e avisa o corretor. Depois dela a
 * proposta vai para "Proposta Transmitida" (8) e isto deixa de valer.
 */
export function isPmeProposalAwaitingTransmission({
  statusPortal,
  hasSignaturesStarted = false,
  allSignaturesCompleted = false,
}: PmeProposalLockInput & { allSignaturesCompleted?: boolean }): boolean {
  return (
    parseStatusPortal(statusPortal) === PortalStatus.ProposalInSignature &&
    hasSignaturesStarted &&
    allSignaturesCompleted
  );
}

/** Status em que a decisão é da mesa de análise, não do corretor. */
const PME_ANALYSIS_STATUSES: readonly PortalStatus[] = [
  PortalStatus.ProposalInAnalysis,
  PortalStatus.ProposalRejectedByAnalysis,
];

export const PME_PROPOSAL_IN_ANALYSIS_SIGNATURE_BLOCKED_REASON =
  'A proposta está em análise. A assinatura fica disponível depois que a análise liberar a proposta.';

/**
 * "Assinatura Digital" bloqueada pela análise: proposta em análise (18) ou
 * recusada por ela (19). A Api já recusa (a assinatura exige 12); aqui o botão
 * só deixa de oferecer o que não vai funcionar.
 */
export function isPmeSignatureBlockedByAnalysis({
  statusPortal,
}: PmeProposalLockInput): boolean {
  const status = parseStatusPortal(statusPortal);
  return status != null && PME_ANALYSIS_STATUSES.includes(status);
}

/**
 * Rótulo de status do card de prêmio do resumo.
 *
 * Enquanto a proposta está em digitação o rótulo é sobre o CÁLCULO (é o que
 * falta o corretor fazer); depois disso quem manda é o statusPortal — e é assim
 * que "Em assinatura" aparece na tela.
 */
export function resolvePmeProposalStatusLabel({
  statusPortal,
  hasSignaturesStarted = false,
  isCalculated = false,
}: PmeProposalLockInput & { isCalculated?: boolean }): string {
  const status = parseStatusPortal(statusPortal);

  // O rótulo global do 7 é "Efetivada", que vem do RD Equipamentos, onde o
  // status é terminal. No PME o 7 significa "aprovada pela subscrição", com
  // assinatura e transmissão ainda por fazer — daí o copy próprio.
  if (status === PortalStatus.ProposalAccepted) {
    return PME_PROPOSAL_ACCEPTED_LABEL;
  }

  if (status != null && status !== PortalStatus.ProposalInProgress) {
    const label = PORTAL_STATUS_LABELS[status];
    if (label) return label;
  }

  // Envelope criado e snapshot ainda em 6: o status real já é "Em assinatura".
  if (hasSignaturesStarted) {
    return PORTAL_STATUS_LABELS[PortalStatus.ProposalInSignature];
  }

  return isCalculated ? 'Pendente de transmissão' : 'Pendente de cálculo';
}

/**
 * O wizard vira só a etapa de Assinatura: status 12, ou envelope já criado
 * enquanto o snapshot ainda não refletiu isso.
 *
 * Proposta encerrada (cancelada, expirada, recusada) nunca entra aqui, mesmo
 * que os envelopes da tentativa anterior ainda existam. A tela fica no resumo,
 * só para visualizar.
 */
export function isPmeProposalSignatureOnly({
  statusPortal,
  hasSignaturesStarted = false,
}: PmeProposalLockInput): boolean {
  if (isTerminalPortalStatus(statusPortal)) return false;

  return (
    parseStatusPortal(statusPortal) === PortalStatus.ProposalInSignature ||
    hasSignaturesStarted
  );
}

/**
 * Fase de assinatura ativa: os envelopes existem e a proposta ainda não passou
 * da assinatura. É o que habilita "Atualizar status" — ação que continua
 * disponível com a proposta já travada para edição.
 */
export function isPmeProposalInSignaturePhase({
  statusPortal,
  hasSignaturesStarted = false,
}: PmeProposalLockInput): boolean {
  if (!hasSignaturesStarted) return false;

  const status = parseStatusPortal(statusPortal);
  if (status == null) return true;

  return (
    PME_ACTIVE_STATUSES.includes(status) ||
    status === PortalStatus.ProposalInSignature
  );
}
